import { Status } from "google-gax";

import { FirestoreErrorCode } from "./firestoreTypes";

import type { ErrorMapping } from "@/errors";
import { ErrorSeverity } from "@/errors";

export const STATUS_TO_STRING_MAP: Record<Status, FirestoreErrorCode> = {
  [Status.CANCELLED]: "cancelled",
  [Status.UNKNOWN]: "unknown",
  [Status.INVALID_ARGUMENT]: "invalid-argument",
  [Status.DEADLINE_EXCEEDED]: "deadline-exceeded",
  [Status.NOT_FOUND]: "not-found",
  [Status.ALREADY_EXISTS]: "already-exists",
  [Status.PERMISSION_DENIED]: "permission-denied",
  [Status.RESOURCE_EXHAUSTED]: "resource-exhausted",
  [Status.FAILED_PRECONDITION]: "failed-precondition",
  [Status.ABORTED]: "aborted",
  [Status.OUT_OF_RANGE]: "out-of-range",
  [Status.UNIMPLEMENTED]: "unimplemented",
  [Status.INTERNAL]: "internal",
  [Status.UNAVAILABLE]: "unavailable",
  [Status.DATA_LOSS]: "data-loss",
  [Status.UNAUTHENTICATED]: "unauthenticated",
  // OK status (not an error, included for completeness)
  [Status.OK]: "unknown" as FirestoreErrorCode,
};

export const FIRESTORE_ERROR_MAPPINGS: Record<
  FirestoreErrorCode,
  ErrorMapping
> = {
  "permission-denied": {
    userMessage: "Unable to access storage. Please try signing in again.",
    internalMessage: "Firestore permission denied",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  unavailable: {
    userMessage:
      "Service temporarily unavailable. Please try again in a moment.",
    internalMessage: "Firestore service unavailable",
    shouldRetry: true,
    severity: ErrorSeverity.Warning,
  },
  "not-found": {
    userMessage: "The requested data could not be found.",
    internalMessage: "Firestore document not found",
    shouldRetry: false,
    severity: ErrorSeverity.Info,
  },
  "deadline-exceeded": {
    userMessage: "The operation took too long. Please try again.",
    internalMessage: "Firestore operation timeout",
    shouldRetry: true,
    severity: ErrorSeverity.Warning,
  },
  unauthenticated: {
    userMessage: "Authentication required. Please sign in again.",
    internalMessage: "Firestore authentication failed",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  "already-exists": {
    userMessage: "This record already exists.",
    internalMessage: "Firestore document already exists",
    shouldRetry: false,
    severity: ErrorSeverity.Info,
  },
  "resource-exhausted": {
    userMessage: "Service is at capacity. Please try again later.",
    internalMessage: "Firestore quota exceeded",
    shouldRetry: true,
    severity: ErrorSeverity.Warning,
  },
  "failed-precondition": {
    userMessage: "The operation cannot be completed at this time.",
    internalMessage: "Firestore precondition failed",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  aborted: {
    userMessage: "The operation was cancelled. Please try again.",
    internalMessage: "Firestore transaction aborted",
    shouldRetry: true,
    severity: ErrorSeverity.Warning,
  },
  "invalid-argument": {
    userMessage: "Invalid data provided. Please check your input.",
    internalMessage: "Firestore invalid argument",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  cancelled: {
    userMessage: "The operation was cancelled.",
    internalMessage: "Firestore operation cancelled",
    shouldRetry: false,
    severity: ErrorSeverity.Info,
  },
  unknown: {
    userMessage: "An unexpected error occurred. Please try again.",
    internalMessage: "Firestore unknown error",
    shouldRetry: true,
    severity: ErrorSeverity.Error,
  },
  "out-of-range": {
    userMessage: "The requested range is invalid.",
    internalMessage: "Firestore out of range",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  unimplemented: {
    userMessage: "This feature is not yet available.",
    internalMessage: "Firestore operation not implemented",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
  internal: {
    userMessage: "An internal error occurred. Our team has been notified.",
    internalMessage: "Firestore internal error",
    shouldRetry: true,
    severity: ErrorSeverity.Error,
  },
  "data-loss": {
    userMessage: "Data integrity error. Please contact support.",
    internalMessage: "Firestore data loss detected",
    shouldRetry: false,
    severity: ErrorSeverity.Error,
  },
};
